import {
	Modal,
	Flex,
	FlexChild,
	Typography,
	TextColors,
	FontFamilies,
	BackgroundColors,
	BorderColors,
	Loading,
	BorderRadius,
	Tag,
	ForegroundColors,
	Button,
	ButtonSize,
} from "@abel-technologies-as/components";
import { DhcProfile, ModalProps } from "@/shared/types";
import { ApprovePayout, CalendarIcon, CheckMarkCircle, RedCircleClose } from "@/shared/icons";
import { AvatarWithFallback, ModalHeader } from "@/shared/components";
import { ModalBody, FirstColumn, Box, IconBox, BoxesWrapper, SecondColumn } from "./style";
import { useTranslation } from "react-i18next";
import { PayoutTable } from "@/modules/dhcs";
import { useGetDhcPayoutApproval } from "@/modules/dhcs/hooks";
import { useState } from "react";
import { ConfirmPayout } from "../confirm-payout/ConfirmPayout";

type ApprovePayoutModalProps = ModalProps & {
	dhcs: DhcProfile[];
};

export const ApprovePayoutModal = ({ open, setOpen, dhcs }: ApprovePayoutModalProps) => {
	const { t, i18n } = useTranslation("common");
	const { t: tDhcs } = useTranslation("dhc");
	const dhcIds = dhcs.map((d) => d.dhcId);
	const { data, isLoading } = useGetDhcPayoutApproval(dhcIds, i18n.language, open);
	const [dhcStep, setDhcStep] = useState(0);
	const [isConfirmaPayoutOpen, setIsConfirmaPayoutOpen] = useState(false);

	const totalSteps = data?.result?.length ?? 0;

	const isFirstStep = dhcStep === 0;
	const isLastStep = dhcStep === totalSteps - 1;

	const currentDhcIds = data?.result.map((d) => d.dhcId).map(String) ?? [];
	const filterPayoutsData = dhcs
		.filter((d) => currentDhcIds.includes(String(d.dhcId)))
		.map((d) => ({
			name: d.name,
			id: d.dhcId,
			email: d.email,
			role: d.role,
			imageUrl: d.imageUrl,
			finalPayout: "kr 43,750",
		}));

	const currentDhcData = data?.result?.[dhcStep];
	const currentProfile = filterPayoutsData[dhcStep] ?? dhcs[0];
	const stats = currentDhcData?.stats;

	const onPrevious = () => {
		if (!isFirstStep) setDhcStep((s) => s - 1);
	};

	const onContinue = () => {
		if (isLastStep) {
			setIsConfirmaPayoutOpen(true);
		} else {
			setDhcStep((s) => s + 1);
		}
	};

	return (
		<>
			<ConfirmPayout
				open={isConfirmaPayoutOpen}
				setOpen={setIsConfirmaPayoutOpen}
				payoutData={filterPayoutsData}
				onSubmitPayout={() => {
					setIsConfirmaPayoutOpen(false);
					setOpen(false);
				}}
			/>
			<Modal
				modalFooter={
					<Flex height="100px" gap="12px" alignItems="center" justifyContent="flex-end">
						<Button
							borderSize={"1px"}
							borderRadius={`${BorderRadius.MD}px`}
							onClick={() => setOpen(false)}
							boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
							borderColor={BorderColors.Primary}>
							<Typography
								lineHeight="22px"
								color={TextColors.Secondary}
								fontSize="16px"
								fontWeight={"600"}
								margin="0 0 0 6px"
								variant="p">
								{t("cancel", { ns: "common" })}
							</Typography>
						</Button>
						{filterPayoutsData.length > 0 && (
							<>
								<Button
									borderSize={"1px"}
									borderRadius={`${BorderRadius.MD}px`}
									onClick={onPrevious}
									disabled={isFirstStep}
									boxShadow={"0px 1px 2px 0px rgba(16, 24, 40, 0.05)"}
									borderColor={BorderColors.Primary}>
									<Typography
										lineHeight="22px"
										color={TextColors.Secondary}
										fontSize="16px"
										fontWeight={"600"}
										margin="0 0 0 6px"
										variant="p">
										{t("previous", { ns: "common" })}
									</Typography>
								</Button>
								<Button
									textColor="white"
									filled
									borderSize={"1px"}
									size={ButtonSize.MEDIUM}
									onClick={onContinue}
									borderRadius={`${BorderRadius.MD}px`}
									backgroundColor={BackgroundColors.BluePrimary}
									borderColor={BackgroundColors.BluePrimary}>
									<Typography lineHeight="22px" fontSize="16px" fontWeight={"600"} margin="0 0 0 6px" variant="p">
										{t("continue", { ns: "common" })}
									</Typography>
								</Button>
							</>
						)}
					</Flex>
				}
				onClose={() => setOpen(false)}
				isOpen={open}
				width="99vw"
				height="100vh"
				modalHeader={
					<Flex justifyContent="flex-start" width="100%">
						<ModalHeader
							icon={<ApprovePayout width="24px" height="24" />}
							subTitle={t("please_review_before_approval")}
							title={t("approve_payout") + ` ${dhcStep + 1}/${totalSteps}`}
						/>
					</Flex>
				}>
				<ModalBody className="w-56">
					{isLoading ? (
						<Flex height="100%" width="100%">
							<Loading variant="indeterminate" size="50px" />
						</Flex>
					) : (
						<Flex height="100%" justifyContent="space-between">
							<FlexChild height="100%" flexBasis="22%" flexGrow={0} flexShrink={0}>
								<FirstColumn>
									<Flex gap="16px" margin="0 0 32px 0">
										<AvatarWithFallback size={56} imageUrl={currentProfile?.imageUrl || ""} />
										<FlexChild flexBasis="1" flexGrow={1} flexShrink={1}>
											<Flex flexDirection="column" alignItems="flex-start">
												<Flex gap="8px" justifyContent="space-between">
													<Typography
														color={TextColors.Secondary}
														fontWeight={600}
														fontSize="18px"
														fontFamily={FontFamilies.InterSemiBold}>
														{currentProfile.name}
													</Typography>
													<Tag
														borderRadius={`${BorderRadius.FULL}px`}
														padding={"1px 7.4px"}
														fontSize={"12px"}
														fontWeight={500}
														lineHeight={"18px"}
														text={currentProfile.role || "DHC"}
														backgroundColor={BackgroundColors.BrandUtility50}
														borderColor={BorderColors.Brand50}
														textColor={TextColors.BrandUtility700}
													/>
												</Flex>
												<Typography
													fontWeight={400}
													fontSize="16px"
													color={TextColors.Tertiary}
													fontFamily={FontFamilies.InterRegular}
													textAlign="left">
													{currentProfile.email}
												</Typography>
											</Flex>
										</FlexChild>
									</Flex>
									<BoxesWrapper>
										<Flex flexDirection="column" gap="12px">
											<Box>
												<Flex justifyContent="flex-start" gap="12px" margin="0 0 12px 0">
													<IconBox backgroundColor={BackgroundColors.BluePrimary}>
														<ApprovePayout color="white" width="24px" height="24px" />
													</IconBox>
													<Typography
														fontSize="16px"
														fontWeight={500}
														color={TextColors.Tertiary}
														fontFamily={FontFamilies.InterMedium}>
														{tDhcs("avg_rating")}
													</Typography>
												</Flex>
												<Typography
													fontSize="36px"
													fontWeight={400}
													letterSpacing="-2%"
													color={TextColors.Primary}
													fontFamily={"Branding-Hedvig"}>
													{stats?.averageRating ?? "—"}
												</Typography>
											</Box>
											<Box>
												<Flex justifyContent="flex-start" gap="12px" margin="0 0 12px 0">
													<IconBox
														border={BorderColors.Secondary}
														boxShadow="0px 1px 2px 0px rgba(16, 24, 40, 0.05);"
														backgroundColor={TextColors.White}>
														<CalendarIcon color={ForegroundColors.Secondary} width="24px" height="24px" />
													</IconBox>
													<Typography
														fontSize="16px"
														fontWeight={500}
														color={TextColors.Tertiary}
														fontFamily={FontFamilies.InterMedium}>
														{tDhcs("weeks")}
													</Typography>
												</Flex>
												<Typography
													fontSize="36px"
													fontWeight={400}
													letterSpacing="-2%"
													color={TextColors.Primary}
													fontFamily={"Branding-Hedvig"}>
													{stats?.totalWeeks ?? "—"}
												</Typography>
											</Box>
											<Box>
												<Flex justifyContent="flex-start" gap="12px" margin="0 0 12px 0">
													<IconBox backgroundColor={BackgroundColors.SuccessSolid}>
														<CheckMarkCircle width="24px" height="24px" color={TextColors.White} />
													</IconBox>
													<Typography
														fontSize="16px"
														fontWeight={500}
														color={TextColors.Tertiary}
														fontFamily={FontFamilies.InterMedium}>
														{tDhcs("rated")}
													</Typography>
												</Flex>
												<Typography
													fontSize="36px"
													fontWeight={400}
													letterSpacing="-2%"
													color={TextColors.Primary}
													fontFamily={"Branding-Hedvig"}>
													{stats?.rated ?? "—"}
												</Typography>
											</Box>
											<Box>
												<Flex justifyContent="flex-start" gap="12px" margin="0 0 12px 0">
													<IconBox backgroundColor={BackgroundColors.ErrorSolid}>
														<RedCircleClose />
													</IconBox>
													<Typography
														fontSize="16px"
														fontWeight={500}
														color={TextColors.Tertiary}
														fontFamily={FontFamilies.InterMedium}>
														{tDhcs("unrated")}
													</Typography>
												</Flex>
												<Typography
													fontSize="36px"
													fontWeight={400}
													letterSpacing="-2%"
													color={TextColors.Primary}
													fontFamily={"Branding-Hedvig"}>
													{stats?.unrated ?? "—"}
												</Typography>
											</Box>
										</Flex>
									</BoxesWrapper>
								</FirstColumn>
							</FlexChild>

							<FlexChild height="100%" flexBasis="80%" flexGrow={0} flexShrink={0}>
								<SecondColumn>
									<Flex flexDirection="column" gap="32px" justifyContent="flex-start" alignItems="flex-start">
										{currentDhcData?.endUsers?.length ? (
											currentDhcData.endUsers.map((item) => (
												<PayoutTable key={item.endUserId} item={item} dhcId={currentProfile.id} />
											))
										) : (
											<Flex width="100%" height="100%" justifyContent="center" alignItems="center">
												<Typography>{tDhcs("no_payout_found")}</Typography>
											</Flex>
										)}
									</Flex>
								</SecondColumn>
							</FlexChild>
						</Flex>
					)}
				</ModalBody>
			</Modal>
		</>
	);
};
